import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { PropertyService } from '@vsolv/packages/properties/web';
import { Claim } from '@wsphere/warranties/domain';
import { BehaviorSubject, catchError, distinctUntilChanged, map, of, shareReplay, switchMap } from 'rxjs';
import { CLAIM_INTAKE_CONFIG } from '../../config/claim-intake.config';
import { UsageDimension } from '../../dialogs/claim-intake/claim-intake.dialog';

const numberOrNull = (value: unknown) =>
  value === null || value === undefined || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-intake-card',
  templateUrl: './claim-intake-card.component.html',
  standalone: false,
})
export class ClaimIntakeCardComponent {
  constructor(private propertySvc: PropertyService) {}

  @PropertyListener('claim') private claim$ = new BehaviorSubject<Claim.Model | null>(null);
  @Input() claim: Claim.Model | null = null;

  /** Staff with clm_EditClaim; the card also checks the claim is open. */
  @Input() canEdit = false;

  @Output() saved = new EventEmitter<void>();

  readonly config = CLAIM_INTAKE_CONFIG;

  readonly callerTypeLabels: Record<Claim.CallerType, string> = {
    [Claim.CallerType.CONTRACT_HOLDER]: 'Contract holder',
    [Claim.CallerType.SERVICE_PROVIDER]: 'Service provider',
    [Claim.CallerType.OTHER]: 'Other',
  };

  // Number readings (odometer, engine hours…) the warranty was sold with, and their value at sale.
  readonly usageDimensions$ = this.claim$.pipe(
    // The claim's own asset values; claims without them fall back to the warranty's.
    map(claim => claim?.propertySetSubmission ?? claim?.warranty?.propertySetSubmission ?? null),
    distinctUntilChanged((before, after) => before?.id === after?.id && before?.setId === after?.setId),
    switchMap(async submission => {
      if (!submission?.setId) return [] as UsageDimension[];
      const set = await this.propertySvc.retrievePropertySet(submission.setId);
      return (set.dimensions ?? [])
        .filter(assignment => assignment.dimension?.isScalar)
        .map(assignment => ({
          id: assignment.dimensionId,
          title: assignment.dimension?.title ?? assignment.dimensionId,
          atSale: numberOrNull(submission.dimensionValues?.[assignment.dimensionId]),
        }));
    }),
    catchError(() => of([] as UsageDimension[])),
    shareReplay(1)
  );

  /** Once authorized, only while money is out and a new job waits to be authorized, as on the server. */
  isEditable(claim: Claim.Model) {
    if (!this.canEdit || claim.status !== Claim.Status.OPEN) return false;
    if (!claim.frozen) return true;

    return !!claim.hasSentPayments && !!claim.items?.some(item => !item.authorizationId && !item.deleted);
  }

  usageTitle(dimensions: UsageDimension[], dimensionId: string) {
    return dimensions.find(dimension => dimension.id === dimensionId)?.title ?? 'Usage';
  }

  /** Phone and city, e.g. "(555) 123-4567 · Austin, TX". */
  describeProvider(provider: Claim.ServiceProviderSnapshot) {
    const place = [provider.address?.city, provider.address?.state].filter(Boolean).join(', ');
    return [provider.phone, place].filter(Boolean).join(' · ');
  }
}
